<template>
  <div class="ibox">
    <div class="ibox-content">

      <template v-if="loaded">

        <AccountSummary :account="account"
                        :partner="currentPartner"
                        :products-count="contractedCount"
                        :trial-count="trialCount"/>

        <TabContainer>

          <Tabs id="general" name="Geral" start-active>
            <TabGeneral :account="account"
                        :tags="tags"
                        @associate-tags="associateTags"
                        @reload-tags="refreshTags"/>
          </Tabs>

          <Tabs id="products" name="Produtos" :quantity="contractedCount">
            <TabProducts :account="account"
                         :apps="apps"
                         :pristine-apps="pristineApps"
                         :transitions="transitions"/>
          </Tabs>

          <Tabs id="settings" name="Configurações">
            <TabSettings :account="account" :pristine-metadata="pristineMetadata"/>
          </Tabs>

          <Tabs id="contract" name="Contrato" :quantity="additivesCount">
            <TabContract :account="account" @retain="retain"/>
          </Tabs>

          <Tabs id="partner" name="White label">
            <TabPartner :account="account" :partners="partners" :has-changes="hasChanges"/>
          </Tabs>

        </TabContainer>

        <div class="footer-actions">
          <span v-if="hasChanges" class="pending">
            <alert-circle-icon size="1x" class="mr-1"/>
            Alterações não salvas — clique em Salvar para aplicar
          </span>

          <div class="footer-right">
            <button @click="cancel" class="btn btn-outline mr-1">Cancelar</button>
            <button @click="save" :disabled="!hasChanges" class="btn btn-primary">Salvar</button>
          </div>
        </div>

      </template>

    </div>
  </div>
</template>

<script>
  import { mapMutations } from 'vuex'
  import { AlertCircleIcon } from 'vue-feather-icons'

  import ModelMapper from '@/mixins/ModelMapper'
  import GetAccountApps from '@/mixins/GetAccountApps'
  import EntityProxy from '@/models/EntityProxy'
  import { buildSavePayload, toPlainAccount } from '@/models/accountPayload'
  import { pendingProductChanges } from '@/models/accountProducts'
  import {
    additivePayload,
    clearPendingFiles,
    isPending,
    pendingContractChanges
  } from '@/models/accountContract'

  import TabContainer from '@/components/shares/TabContainer'
  import Tabs from '@/components/shares/Tab'

  import AccountSummary from '@/components/admin/accounts/show/AccountSummary'
  import TabContract from '@/components/admin/accounts/show/TabContract'
  import TabGeneral from '@/components/admin/accounts/show/TabGeneral'
  import TabPartner from '@/components/admin/accounts/show/TabPartner'
  import TabProducts from '@/components/admin/accounts/show/TabProducts'
  import TabSettings from '@/components/admin/accounts/show/TabSettings'

  import { ACCOUNT_STATUS } from '@/utils/accountStatus'
  import {
    NOTICE_WITH_DATE,
    applyNoticeToContract,
    daysUntil,
    noticeExpired
  } from '@/models/accountRetention'

  const TAG_PATH = 'admin/account_tags'

  const ACTION_PATH = 'admin/account_apps'

  const ACCOUNT_PATH = 'admin/accounts'

  const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } }

  const RETAINED_NOTICE = 'Cliente retido: a conta voltou para Ativa e a contagem para exclusão foi cancelada.'

  const IGNORED_ON_COMPARE = ['created_at', 'tags', 'updated_at']

  const LIST_FIELDS = ['account_apps', 'contract_additives', 'tags']

  const clone = value => (value === null || value === undefined ? value : JSON.parse(JSON.stringify(value)))

  const documentPendingFile = account => {
    const file = (account.contract_document || {}).file

    return isPending(file) ? file : null
  }

  const reloadedValue = (fresh, field) => {
    const value = fresh[field]

    if (value !== undefined && value !== null)
      return value

    return LIST_FIELDS.includes(field) ? [] : null
  }

  const RELOADED_FIELDS = [
    'account_apps',
    'contract_additives',
    'contract_document',
    'deletion_base_at',
    'subscription_notice_end_date',
    'subscription_notice_mode',
    'subscription_type',
    'tags'
  ]

  export default {
    components: {
      AccountSummary, AlertCircleIcon, TabContainer, TabContract,
      TabGeneral, TabPartner, TabProducts, TabSettings, Tabs
    },
    computed: {
      additivesCount() {
        return (this.account.contract_additives || []).length
      },
      apps() {
        return (this.getApps || []).filter(app => app.appType === 'app')
      },
      contractedCount() {
        const productIds = this.apps.map(app => app.id)

        return (this.account.account_apps || [])
          .filter(accountApp => productIds.includes(accountApp.app_id))
          .filter(accountApp => accountApp.active && accountApp.status !== 'canceled')
          .length
      },
      currentPartner() {
        if (!this.account.account_id || !this.partners)
          return null

        return this.partners.find(partner => partner.id === this.account.account_id) || null
      },
      hasChanges() {
        if (!this.loaded)
          return false

        return this.comparable(this.account) !== this.comparable(this.pristine)
      },
      pristineApps() {
        return this.pristine.account_apps || []
      },
      pristineMetadata() {
        return this.pristine.metadata || {}
      },
      trialCount() {
        return (this.account.account_apps || [])
          .filter(accountApp => accountApp.status === 'trial')
          .length
      }
    },
    created() {
      this.loadPartners()
      this.loadTags()
      this.loadTransitions()

      if (!this.getApps.length)
        this.loadAppsAndAccountApps()

      this.loadAccount()
    },
    data() {
      return {
        account: {},
        loaded: false,
        modelPathPrefix: 'admin',
        nameModel: 'Account',
        partners: null,
        pristine: {},
        tags: [],
        transitions: {}
      }
    },
    methods: {
      ...mapMutations(['setNameBreadcrumb']),
      applyNoticeExpiration() {
        if (this.account.subscription_type !== ACCOUNT_STATUS.IN_NOTICE)
          return

        if (!noticeExpired(this.account))
          return

        const end = this.account.subscription_notice_end_date

        this.account.subscription_type = ACCOUNT_STATUS.CANCELED
        this.account.deletion_base_at = end

        this.$notify.add(
          `O aviso prévio terminou em ${this.$options.filters.date(end)}: ` +
          'a conta foi movida para Cancelada e a contagem para exclusão começou.'
        )
      },
      associateTags(tagIds) {
        this.axios.post(`admin/accounts/${this.account.id}/set_tags`, { tag_ids: tagIds })
          .then(() => {
            const linked = this.tags.filter(tag => tagIds.includes(tag.id))

            this.$set(this.account, 'tags', linked)
            this.$set(this.pristine, 'tags', linked)
          })
          .catch(e => this.$notify.addError(e))
      },
      buildData(model) {
        return model
      },
      cancel() {
        this.$router.push('/admin/accounts')
      },
      comparable(account) {
        const copy = { ...account }
        IGNORED_ON_COMPARE.forEach(field => delete copy[field])

        return JSON.stringify(copy)
      },
      loadAccount() {
        this.asyncTemplateShow(this.id, {
          after: ({ model }) => {
            this.account = this.withContractFields(toPlainAccount(model))
            this.applyNoticeExpiration()
            applyNoticeToContract(this.account)
            this.snapshot()
            this.loaded = true
            this.setNameBreadcrumb(this.account.name)
          }
        })
      },
      loadPartners() {
        const data = this.$ls.get('adminPartnerAccounts')
        if (data)
          return this.partners = EntityProxy.build(data)

        this.axios.get('admin/accounts?q[account_type]=partner&meta=false&per_page=10000')
          .then(response => response.data)
          .then(data => {
            this.partners = EntityProxy.build(data)
            this.$ls.set('adminPartnerAccounts', data, { minutes: 1 })
          })
      },
      loadTags() {
        return this.axios.get(TAG_PATH, { params: { meta: false, per_page: 200 } })
          .then(response => this.tags = response.data || [])
          .catch(e => this.$notify.addError(e))
      },
      loadTransitions() {
        return this.axios.get(`${ACTION_PATH}/transitions`)
          .then(response => this.transitions = response.data || {})
          .catch(e => this.$notify.addError(e))
      },
      noticeError() {
        if (this.account.subscription_type !== ACCOUNT_STATUS.IN_NOTICE)
          return null

        if (!this.account.subscription_notice_mode)
          return 'Informe o aviso prévio: ausente ou com data de encerramento.'

        if (this.account.subscription_notice_mode !== NOTICE_WITH_DATE)
          return null

        if (!this.account.subscription_notice_end_date)
          return 'Informe até quando o aviso prévio é válido.'

        if (daysUntil(this.account.subscription_notice_end_date) < 0)
          return 'O aviso prévio termina numa data passada. Ajuste a data antes de salvar.'

        return null
      },
      refreshTags() {
        return this.loadTags().then(() => {
          const linked = (this.account.tags || [])
            .map(tag => this.tags.find(candidate => candidate.id === tag.id))
            .filter(Boolean)

          this.$set(this.account, 'tags', linked)
          this.$set(this.pristine, 'tags', linked)
        })
      },
      additiveFormData(additive, file, removeFile) {
        const form = new FormData()
        const payload = additivePayload(additive)

        Object.keys(payload).forEach(key => form.append(key, payload[key] === null ? '' : payload[key]))

        if (file)
          form.append('file', file)

        if (removeFile)
          form.append('remove_file', 'true')

        return form
      },
      additivesPath(additive) {
        return `${ACCOUNT_PATH}/${this.id}/contract_additives${additive && additive.id ? `/${additive.id}` : ''}`
      },
      async applyContractChanges() {
        const changes = pendingContractChanges(this.account, this.pristine)

        for (const change of changes)
          await this.applyContractChange(change)

        return changes
      },
      applyContractChange({ action, additive, file, removeFile }) {
        const filePath = `${ACCOUNT_PATH}/${this.id}/contract_file`

        if (action === 'attach_file') {
          const form = new FormData()
          form.append('file', file)

          return this.axios.post(filePath, form, MULTIPART)
        }

        if (action === 'remove_file')
          return this.axios.delete(filePath)

        if (action === 'remove_additive')
          return this.axios.delete(this.additivesPath(additive))

        const form = this.additiveFormData(additive, file, removeFile)

        if (action === 'create_additive')
          return this.axios.post(this.additivesPath(null), form, MULTIPART)

        return this.axios.put(this.additivesPath(additive), form, MULTIPART)
      },
      async retain() {
        this.openLoad()

        try {
          await this.axios.post(`${ACCOUNT_PATH}/${this.id}/retain`)

          this.syncPristine(await this.reloadSavedFields())
          this.$notify.add(RETAINED_NOTICE)
        } catch (e) {
          this.$notify.addError(e)
        } finally {
          this.closeLoad()
        }
      },
      async applyProductChanges() {
        const changes = pendingProductChanges(this.account.account_apps, this.pristineApps)

        for (const change of changes) {
          await this.axios.post(`${ACTION_PATH}/${change.action}`, {
            account_id: this.account.id,
            app_id: change.app_id,
            expiration_at: change.expiration_at
          })
        }

        return changes
      },
      async reloadSavedFields() {
        const response = await this.axios.get(`${ACCOUNT_PATH}/${this.id}`)
        const fresh = this.withContractFields(toPlainAccount(response.data))

        RELOADED_FIELDS.forEach(field => this.$set(this.account, field, reloadedValue(fresh, field)))
        this.account.updated_at = fresh.updated_at

        return fresh
      },
      async save() {
        const invalid = this.noticeError()

        if (invalid)
          return this.$notify.addError(invalid)

        this.openLoad()

        try {
          await this.applyProductChanges()

          await this.axios.put(`${ACCOUNT_PATH}/${this.id}`, buildSavePayload(this.account))

          await this.applyContractChanges()

          await this.reloadSavedFields()

          clearPendingFiles()
          this.snapshot()
          this.$notify.add('Alterações salvas com sucesso.')
        } catch (e) {
          await this.resyncAfterFailure()
          this.$notify.addError(e)
          this.$notify.addError('Não foi possível salvar as alterações.')
        } finally {
          this.closeLoad()
        }
      },
      async resyncAfterFailure() {
        const pendingFile = documentPendingFile(this.account)

        try {
          this.syncPristine(await this.reloadSavedFields())

          if (pendingFile)
            this.$set(this.account.contract_document, 'file', pendingFile)
        } catch (e) {
          this.$notify.addError(e)
        }
      },
      syncPristine(fresh) {
        RELOADED_FIELDS.forEach(field => this.$set(this.pristine, field, clone(reloadedValue(fresh, field))))
      },
      snapshot() {
        this.pristine = JSON.parse(JSON.stringify(this.account))
      },
      withContractFields(account) {
        const document = account.contract_document || {}

        return {
          subscription_notice_mode: null,
          subscription_notice_end_date: null,
          deletion_base_at: null,
          ...account,
          account_apps: account.account_apps || [],
          contract_additives: account.contract_additives || [],
          contract_document: {
            start_date: null,
            end_date: null,
            renewal_mode: 'automatic',
            file: null,
            ...document
          },
          metadata: account.metadata || {},
          tags: account.tags || []
        }
      }
    },
    mixins: [ModelMapper, GetAccountApps],
    name: 'AdminAccountsShow',
    props: {
      id: { required: true }
    }
  }
</script>

<style scoped>
  .footer-actions {
    position: sticky;
    bottom: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 24px;
    padding: 12px 0;
    border-top: 1px solid var(--border-color);
    background: var(--card-bg);
  }

  .footer-right {
    display: flex;
    align-items: center;
  }

  .pending {
    display: flex;
    align-items: center;
    margin-right: 16px;
    font-size: 13px;
    font-weight: 600;
    color: #B26B00;
  }
</style>
